import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { writeAuditLog } from '@/lib/audit';
import { formatISODate } from '@/lib/salesAnalytics';
import {
  parseItemLedgerFile, fetchExistingEntryNos, importSalesEntries,
  type ParsedSalesEntry, type ParseResult,
} from '@/lib/api/salesEntries';

type Step = 'upload' | 'preview' | 'applying' | 'done';

interface Preview {
  entries: ParsedSalesEntry[];
  newCount: number;
  updateCount: number;
  skipped: ParseResult['skipped'];
  unmappedHeaders: string[];
  fileNames: string[];
  minDate: string;
  maxDate: string;
  units: number;
  revenue: number;
  returns: number;
  /** Rows whose units and revenue disagree in sign - a miscoded credit note. */
  signMismatch: number;
}

export function SalesImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('upload');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState({ imported: 0, failed: 0 });

  const reset = useCallback(() => {
    setStep('upload');
    setPreview(null);
    setErrors([]);
    setProgress({ done: 0, total: 0 });
    setResult({ imported: 0, failed: 0 });
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileErrors: string[] = [];
    const fileNames: string[] = [];
    const allSkipped: ParseResult['skipped'] = [];
    const unmapped = new Set<string>();
    // Last file wins on a duplicate Entry No. across files.
    const byEntryNo = new Map<number, ParsedSalesEntry>();

    for (const file of Array.from(files)) {
      fileNames.push(file.name);
      let parsed: ParseResult;
      try {
        parsed = await parseItemLedgerFile(file);
      } catch (err) {
        fileErrors.push(`${file.name}: could not be read (${err instanceof Error ? err.message : 'unknown error'})`);
        continue;
      }

      if (parsed.missingRequired.length > 0) {
        fileErrors.push(
          `${file.name}: missing required column${parsed.missingRequired.length > 1 ? 's' : ''} — ${parsed.missingRequired.join(', ')}. ` +
          `Export the Item Ledger Entries list with all columns shown.`
        );
        continue;
      }

      for (const entry of parsed.entries) byEntryNo.set(entry.entry_no, entry);
      allSkipped.push(...parsed.skipped);
      for (const h of parsed.unmappedHeaders) unmapped.add(h);
    }

    const entries = [...byEntryNo.values()];
    if (entries.length === 0) {
      setErrors(fileErrors.length > 0 ? fileErrors : ['No sales entries found in the selected file(s).']);
      toast.error('Nothing to import');
      return;
    }

    let existing: Set<number>;
    try {
      existing = await fetchExistingEntryNos(entries.map(e => e.entry_no));
    } catch (err) {
      setErrors([`Could not check existing entries: ${err instanceof Error ? err.message : 'unknown error'}`]);
      toast.error('Could not reach the database');
      return;
    }

    const dates = entries.map(e => e.posting_date).sort();
    setPreview({
      entries,
      newCount: entries.filter(e => !existing.has(e.entry_no)).length,
      updateCount: entries.filter(e => existing.has(e.entry_no)).length,
      skipped: allSkipped,
      unmappedHeaders: [...unmapped],
      fileNames,
      minDate: dates[0],
      maxDate: dates[dates.length - 1],
      units: entries.reduce((a, e) => a + e.units, 0),
      revenue: Math.round(entries.reduce((a, e) => a + e.revenue, 0) * 100) / 100,
      returns: entries.filter(e => e.units < 0).length,
      // A credit note exported with positive units counts as a sale, and
      // is_return - derived from units alone - calls it a sale too. Surfaced
      // rather than auto-corrected: T26580 held a cancelling pair where
      // flipping the sign would have credited two units for one machine.
      signMismatch: entries.filter(e => (e.units > 0 && e.revenue < 0) || (e.units < 0 && e.revenue > 0)).length,
    });
    setErrors(fileErrors);
    setStep('preview');
  };

  const apply = async () => {
    if (!preview) return;
    setStep('applying');
    setProgress({ done: 0, total: preview.entries.length });

    const { imported, failed } = await importSalesEntries(preview.entries, (done, total) =>
      setProgress({ done, total })
    );

    const failedCount = failed.reduce((a, f) => a + f.entryNos.length, 0);
    setResult({ imported, failed: failedCount });
    if (failed.length > 0) {
      setErrors(prev => [...prev, ...failed.map(f => `${f.entryNos.length} entries failed: ${f.message}`)]);
    }

    await writeAuditLog({
      action: 'import',
      entity_type: 'sales_entries',
      entity_label: preview.fileNames.join(', '),
      new_value: {
        imported,
        failed: failedCount,
        new: preview.newCount,
        updated: preview.updateCount,
        range: `${preview.minDate} → ${preview.maxDate}`,
      },
      notes: `Imported BC Item Ledger sales entries (${imported} rows)`,
    });

    setStep('done');
    queryClient.invalidateQueries({ queryKey: ['sales-entries'] });
    queryClient.invalidateQueries({ queryKey: ['sales-date-bounds'] });
    // A PAO page cached earlier in this session shows the sale history for its
    // own serials, and an import is the one thing that can change it.
    queryClient.invalidateQueries({ queryKey: ['pao-detail-sales'] });
    // The sale → assembly order join is scoped by date range, so new entries in
    // an already-viewed range change which orders come back.
    queryClient.invalidateQueries({ queryKey: ['assembly-order-index'] });

    if (failedCount === 0) toast.success(`Imported ${imported} sales entries`);
    else toast.error(`${failedCount} entries failed to import`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import Sales from Business Central
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-3">
              <p className="font-medium">Export the Item Ledger Entries list from BC:</p>
              <ol className="text-muted-foreground text-xs space-y-1 list-decimal list-inside ml-1">
                <li>Open <strong>Item Ledger Entries</strong></li>
                <li>Filter <strong>Entry Type</strong> to <strong>Sale</strong></li>
                <li>Filter <strong>Item Category Code</strong> to <strong>GAMING PCS</strong></li>
                <li><strong>Open in Excel</strong>, then save as .xlsx</li>
              </ol>
              <p className="text-muted-foreground text-xs pt-2 border-t">
                Entries are matched on <strong>Entry No.</strong>, so re-importing an overlapping
                date range updates those rows instead of duplicating them — safe to upload the
                same period twice, and it picks up BC cost revaluations.
              </p>
            </div>

            <label className="flex flex-col items-center gap-2 cursor-pointer border-2 border-dashed rounded-lg p-8 hover:bg-muted/30 transition-colors w-full">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Click to upload</span>
              <span className="text-xs text-muted-foreground">Excel (.xlsx) or CSV — multiple files allowed</span>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.txt,.xlsx,.xls"
                multiple
                className="hidden"
                onChange={handleFiles}
              />
            </label>

            {errors.length > 0 && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 space-y-1">
                {errors.map((err, i) => (
                  <p key={i} className="text-xs text-destructive flex gap-2">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{err}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground flex flex-wrap gap-1 items-center">
              <span>Files:</span>
              {preview.fileNames.map((n, i) => <Badge key={i} variant="outline" className="text-xs">{n}</Badge>)}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="New" value={String(preview.newCount)} />
              <Stat label="Updating" value={String(preview.updateCount)} />
              <Stat label="Net units" value={String(preview.units)} />
              <Stat label="Revenue" value={formatCurrency(preview.revenue)} />
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>
                Posting dates <strong>{formatISODate(preview.minDate)}</strong> to{' '}
                <strong>{formatISODate(preview.maxDate)}</strong>
              </p>
              {preview.returns > 0 && (
                <p>
                  Includes <strong>{preview.returns}</strong> return{preview.returns > 1 ? 's' : ''} —
                  counted as negative units and revenue.
                </p>
              )}
              {preview.signMismatch > 0 && (
                <p className="text-destructive">
                  <strong>{preview.signMismatch}</strong> row{preview.signMismatch > 1 ? 's have' : ' has'} units
                  and revenue with opposite signs — usually a credit note exported with positive units, which
                  would be counted as a sale. Check these in BC before importing; review them afterwards with{' '}
                  <code>select * from sales_entry_anomalies</code>.
                </p>
              )}
            </div>

            {preview.skipped.length > 0 && (
              <details className="rounded-lg border p-3">
                <summary className="text-xs font-medium cursor-pointer">
                  {preview.skipped.length} row{preview.skipped.length > 1 ? 's' : ''} skipped
                </summary>
                <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                  {preview.skipped.slice(0, 100).map((s, i) => (
                    <li key={i} className="text-xs text-muted-foreground">Row {s.row}: {s.reason}</li>
                  ))}
                  {preview.skipped.length > 100 && (
                    <li className="text-xs text-muted-foreground">…and {preview.skipped.length - 100} more</li>
                  )}
                </ul>
              </details>
            )}

            {preview.unmappedHeaders.length > 0 && (
              <details className="rounded-lg border p-3">
                <summary className="text-xs font-medium cursor-pointer text-muted-foreground">
                  {preview.unmappedHeaders.length} column{preview.unmappedHeaders.length > 1 ? 's' : ''} in the file are not imported
                </summary>
                <p className="mt-2 text-xs text-muted-foreground">{preview.unmappedHeaders.join(', ')}</p>
              </details>
            )}

            {errors.length > 0 && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 space-y-1">
                {errors.map((err, i) => (
                  <p key={i} className="text-xs text-destructive flex gap-2">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{err}</span>
                  </p>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={reset}>Choose different files</Button>
              <Button onClick={apply}>Import {preview.entries.length} entries</Button>
            </div>
          </div>
        )}

        {step === 'applying' && (
          <div className="py-10 flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Importing {progress.done} of {progress.total}…
            </p>
          </div>
        )}

        {step === 'done' && (
          <div className="space-y-4">
            <div className="py-6 flex flex-col items-center gap-3">
              {result.failed === 0
                ? <CheckCircle2 className="h-10 w-10" style={{ color: 'var(--delta-good)' }} />
                : <AlertCircle className="h-10 w-10 text-destructive" />}
              <p className="text-sm font-medium">
                {result.imported} entr{result.imported === 1 ? 'y' : 'ies'} imported
              </p>
              {result.failed > 0 && (
                <p className="text-sm text-destructive">{result.failed} failed</p>
              )}
            </div>

            {errors.length > 0 && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 space-y-1">
                {errors.map((err, i) => (
                  <p key={i} className="text-xs text-destructive">{err}</p>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={reset}>Import more</Button>
              <Button onClick={() => { reset(); onOpenChange(false); }}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
